import {useState} from 'react'
import type {PublicUser} from '../../../types/public-user'
import styles from './UserInitialMessage.module.css'
import type {Session} from '../../../types/session'
import {ExpandableMarkdown} from '../../expandable-markdown/ExpandableMarkdown'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {ownerAvatarPath} from '@github-ui/paths'
import {useAgentInfo} from '@github-ui/copilot-shared-ui/agents/use-agent-info'
import {createMarkdownWithAgentLinks} from '../../../utils/markdown-link-utils'
import {AgentAvatar} from '../../AgentAvatar'
import {EventInformation, SessionMetadataRow, type SessionEnvironment} from './SessionMetadataRow'

function getDisplayName(
  isCurrentUserTheCreator: boolean,
  session: Session,
  isTaskCreatedByAutomation?: boolean,
  customAgentDisplayName?: string,
) {
  if (isTaskCreatedByAutomation) return customAgentDisplayName ?? 'Automation'
  if (isCurrentUserTheCreator) return 'You'
  if (session.user_login) return session.user_login
  return 'User'
}

function getAvatarUrl(session: Session, currentUser?: PublicUser, isCurrentUserTheCreator?: boolean) {
  // Prefer the current user's avatar URL for enterprise correctness
  if (isCurrentUserTheCreator && currentUser?.primaryAvatarUrl) return currentUser.primaryAvatarUrl
  return ownerAvatarPath({owner: session.user_login ?? 'ghost'})
}

interface UserInitialMessageProps {
  threadId?: string
  currentUser?: PublicUser
  session: Session
  message?: string
  environment?: SessionEnvironment
  agentId?: number | null
  showSessionMetadata?: boolean
  suppressPrompt?: boolean
  isTaskCreatedByAutomation?: boolean
  automationDisplayName?: string
  automationPrompt?: string
}

export const UserInitialMessage = ({
  currentUser,
  threadId,
  session,
  message,
  environment = 'cloud',
  agentId,
  showSessionMetadata = false,
  suppressPrompt = false,
  isTaskCreatedByAutomation,
  automationDisplayName,
  automationPrompt,
}: UserInitialMessageProps) => {
  const [promptHasFocus, setPromptHasFocus] = useState(false)
  const {data: agentInfo, isLoading: isAgentInfoLoading} = useAgentInfo(
    agentId,
    showSessionMetadata ? session.agent_slug : undefined,
  )
  const content = session.event_content
  // Scheduled/manual automation runs have no triggering resource, so
  // event_content is empty. Fall back to the automation's configured prompt
  // so the initial message shows what the automation was asked to do.
  const bodyContent = content || automationPrompt
  const isCurrentUserTheCreator = !isTaskCreatedByAutomation && currentUser?.id === session.user_id
  const displayName = getDisplayName(isCurrentUserTheCreator, session, isTaskCreatedByAutomation, automationDisplayName)
  const avatarUrl = getAvatarUrl(session, currentUser, isCurrentUserTheCreator)

  if (showSessionMetadata) {
    return (
      <div className={styles.metadataContainer} data-testid="user-initial-message">
        <SessionMetadataRow
          currentUser={currentUser}
          threadId={threadId}
          session={session}
          environment={environment}
          message={message}
          agentInfo={agentInfo}
          isAgentInfoLoading={isAgentInfoLoading}
          isTaskCreatedByAutomation={isTaskCreatedByAutomation}
          automationDisplayName={automationDisplayName}
        />
        {(!suppressPrompt || promptHasFocus) && bodyContent && (
          <div
            className={styles.metadataContent}
            onFocusCapture={() => setPromptHasFocus(true)}
            onBlurCapture={event => {
              if (event.relatedTarget === null && !document.hasFocus()) return
              if (!event.currentTarget.contains(event.relatedTarget)) setPromptHasFocus(false)
            }}
          >
            <div className={styles.messageBubble}>
              <ExpandableMarkdown
                className={styles.agentMentionText}
                markdown={agentInfo ? createMarkdownWithAgentLinks(bodyContent, agentInfo) : bodyContent}
              />
            </div>
            <div className={styles.avatarContainer}>
              {isTaskCreatedByAutomation ? (
                <AgentAvatar size={24} agentId={agentId ?? undefined} />
              ) : (
                <GitHubAvatar src={avatarUrl} size={24} />
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.messageBubble}>
        <p className={`${styles.meta}${bodyContent ? ` ${styles.metaSmall}` : ''}`}>
          <span>{displayName}</span> {message}
          <EventInformation session={session} threadId={threadId} />
        </p>

        {bodyContent && (
          <ExpandableMarkdown
            className={styles.agentMentionText}
            markdown={agentInfo ? createMarkdownWithAgentLinks(bodyContent, agentInfo) : bodyContent}
          />
        )}
      </div>
      <div className={styles.avatarContainer}>
        {isTaskCreatedByAutomation ? (
          <AgentAvatar size={24} agentId={agentId ?? undefined} />
        ) : (
          <GitHubAvatar src={avatarUrl} size={24} />
        )}
      </div>
    </div>
  )
}
